import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2, Save, X } from 'lucide-react';
import { toast } from 'sonner';

interface DealerNotesPanelProps {
  dealerName: string;
  initialNotes?: {
    advisor_notes?: string | undefined;
    technician_notes?: string | undefined;
  };
  onSave: (notes: { advisor_notes: string; technician_notes: string }) => Promise<void>;
  onCancel: () => void;
  className?: string;
  /** View-only (demo tenants): fields disabled, no Save button */
  readOnly?: boolean;
}

// eslint-disable-next-line max-lines-per-function
export const DealerNotesPanel: React.FC<DealerNotesPanelProps> = ({
  dealerName,
  initialNotes = {},
  onSave,
  onCancel,
  className = '',
  readOnly = false
}) => {
  const [advisorNotes, setAdvisorNotes] = useState(initialNotes.advisor_notes || '');
  const [technicianNotes, setTechnicianNotes] = useState(initialNotes.technician_notes || '');
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Update local state when initialNotes change
  useEffect(() => {
    setAdvisorNotes(initialNotes.advisor_notes || '');
    setTechnicianNotes(initialNotes.technician_notes || '');
  }, [initialNotes]);

  // Track changes
  useEffect(() => {
    const hasAdvisorChanges = advisorNotes !== (initialNotes.advisor_notes || '');
    const hasTechChanges = technicianNotes !== (initialNotes.technician_notes || '');
    setHasChanges(hasAdvisorChanges || hasTechChanges);
  }, [advisorNotes, technicianNotes, initialNotes]);

  const handleSave = async () => {
    if (!hasChanges) {
      onCancel();
      return;
    }

    setIsSaving(true);
    try {
      await onSave({
        advisor_notes: advisorNotes.trim(),
        technician_notes: technicianNotes.trim()
      });
    } catch (error) {
      console.error('Failed to save notes:', error);
      toast.error('Failed to save notes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Note: Removed confirm dialog to avoid blocking user flow
    // Changes are automatically tracked and can be reverted if needed
    onCancel();
  };

  return (
    <div className={`border rounded-lg p-4 bg-card space-y-4 ${className}`}>
      <div className='flex items-center justify-between'>
        <h4 className='text-sm font-medium text-foreground'>Notes for {dealerName}</h4>
        <Button variant='ghost' size='sm' onClick={handleCancel} className='h-6 w-6 p-0'>
          <X className='h-4 w-4' />
        </Button>
      </div>

      <div className='space-y-3'>
        {/* Advisor Notes */}
        <div className='space-y-2'>
          <Label
            htmlFor={`advisor-notes-${dealerName}`}
            className='text-xs font-medium text-blue-700 dark:text-blue-300'
          >
            🔵 Advisor Notes
          </Label>
          <Textarea
            id={`advisor-notes-${dealerName}`}
            placeholder='Notes for advisors at your dealership...'
            value={advisorNotes}
            onChange={e => setAdvisorNotes(e.target.value)}
            className='min-h-[60px] text-sm'
            maxLength={500}
            disabled={isSaving || readOnly}
          />
          <div className='text-xs text-muted-foreground text-right'>{advisorNotes.length}/500</div>
        </div>

        {/* Technician Notes */}
        <div className='space-y-2'>
          <Label
            htmlFor={`tech-notes-${dealerName}`}
            className='text-xs font-medium text-orange-700 dark:text-orange-300'
          >
            🟠 Technician Notes
          </Label>
          <Textarea
            id={`tech-notes-${dealerName}`}
            placeholder='Instructions for technicians (applies to all recalls at this dealer)...'
            value={technicianNotes}
            onChange={e => setTechnicianNotes(e.target.value)}
            className='min-h-[60px] text-sm'
            maxLength={500}
            disabled={isSaving || readOnly}
          />
          <div className='text-xs text-muted-foreground text-right'>
            {technicianNotes.length}/500
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className='flex justify-end gap-2 pt-2 border-t'>
        <Button variant='outline' size='sm' onClick={handleCancel} disabled={isSaving}>
          Cancel
        </Button>
        {!readOnly && (
          <Button
            size='sm'
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            className='min-w-[80px]'
          >
            {isSaving ? (
              <>
                <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                Saving...
              </>
            ) : (
              <>
                <Save className='h-4 w-4 mr-2' />
                Save
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default DealerNotesPanel;
